import React, { useState } from 'react';
import { FaBell, FaUser, FaSignOutAlt, FaCog, FaBars, FaSyncAlt, FaLifeRing, FaPrint } from 'react-icons/fa';
import { FiCheckCircle, FiAlertCircle, FiInfo, FiX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useNavigate } from 'react-router-dom';
import Badge from '../common/Badge';
import Spinner from '../common/Spinner';
import PendingSyncIndicator from '../common/PendingSyncIndicator';
import { usePrintQueueCount } from '../../hooks/usePrintQueue';
import { isManager } from '../../utils/permissions';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, tenant, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, isConnected } = useWebSocket();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const canSeePrintQueue = isManager(user?.role);
  const { data: printQueueCount = 0 } = usePrintQueueCount({ enabled: canSeePrintQueue });

  const deleteNotification = (id: string) => {
    // Note: Delete functionality can be implemented via WebSocket event
    console.log('Delete notification:', id);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <FiCheckCircle className="text-green-600" />;
      case 'error':
        return <FiAlertCircle className="text-red-600" />;
      case 'warning':
        return <FiAlertCircle className="text-yellow-600" />;
      default:
        return <FiInfo className="text-blue-600" />;
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left: Menu button + Logo */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            aria-label="Open navigation menu"
          >
            <FaBars size={20} aria-hidden="true" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Checks & Ticks <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold bg-yellow-100 text-yellow-800 rounded-full uppercase tracking-wide align-top">Beta</span></h1>
            {tenant && <p className="text-sm text-gray-600">{tenant.name}</p>}
          </div>
        </div>

        {/* Right: Pending Sync + Notifications + User menu */}
        <div className="flex items-center gap-4">
          {/* Pending Sync Indicator — shows captured inspections waiting/failed to sync */}
          <PendingSyncIndicator />

          {/* Print Queue Badge — visible to managers when queue is non-empty */}
          {canSeePrintQueue && printQueueCount > 0 && (
            <button
              onClick={() => navigate('/qr-codes/print-queue')}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              aria-label={`Print queue, ${printQueueCount} item${printQueueCount !== 1 ? 's' : ''}`}
            >
              <FaPrint size={20} aria-hidden="true" />
              <span
                className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-blue-600 rounded-full"
                aria-hidden="true"
              >
                {printQueueCount > 9 ? '9+' : printQueueCount}
              </span>
            </button>
          )}

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
              aria-expanded={showNotifications ? 'true' : 'false'}
              aria-haspopup="true"
            >
              <FaBell size={20} aria-hidden="true" />
              {unreadCount > 0 && (
                <span
                  className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-red-500 rounded-full"
                  aria-hidden="true"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowNotifications(false)}
                  aria-hidden="true"
                />
                <div
                  className="absolute right-0 mt-2 w-96 max-h-128 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden flex flex-col"
                  role="region"
                  aria-label="Notifications panel"
                >
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}
                        title={isConnected ? 'Live updates active' : 'Connecting...'}
                      >
                        {isConnected ? '● Live' : '○ Offline'}
                      </span>
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  {/* Notifications List */}
                  <div className="overflow-y-auto flex-1">
                    {notifications.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <FaBell className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-sm">No notifications</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 hover:bg-gray-50 transition-colors ${!notification.is_read ? 'bg-blue-50' : ''
                              }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="shrink-0 mt-1">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-600 mb-2">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(notification.created_at).toLocaleString()}
                                </p>
                              </div>
                              <div className="shrink-0 flex items-start gap-1">
                                {!notification.is_read && (
                                  <button
                                    onClick={() => markAsRead(notification.id)}
                                    className="p-1 text-blue-600 hover:bg-blue-100 rounded-sm"
                                    title="Mark as read"
                                  >
                                    <FiCheckCircle className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => deleteNotification(notification.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                                  title="Delete"
                                >
                                  <FiX className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              aria-label="User menu"
              aria-expanded={showUserMenu ? 'true' : 'false'}
              aria-haspopup="true"
            >
              <FaUser size={18} aria-hidden="true" />
              <span className="hidden md:inline text-sm font-medium">
                {user?.first_name} {user?.last_name}
              </span>
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                  aria-hidden="true"
                />
                <div
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20"
                  role="region"
                  aria-label="User account options"
                >
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-gray-600">{user?.email}</p>
                    <p className="text-xs text-gray-500 mt-1 capitalize">{user?.role}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/support/my-tickets');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaLifeRing size={14} aria-hidden="true" />
                    My Support Tickets
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/settings');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaCog size={14} aria-hidden="true" />
                    Settings
                  </button>
                  <button
                    onClick={async () => {
                      setShowUserMenu(false);
                      try {
                        const { clearDatabase } = await import('../../dry/database');
                        await clearDatabase();
                        window.location.reload();
                      } catch { /* ignore */ }
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaSyncAlt size={14} aria-hidden="true" />
                    Clear Cache
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <FaSignOutAlt size={14} aria-hidden="true" />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
